import {
    MAX_NOTIFICATION_DISPLAY_LENGTH,
    TAB_RECEIVED_FEEDBACK,
    TAB_RECEIVED_REQUEST,
    TAB_SENT_FEEDBACK,
    TAB_SENT_REQUEST,
} from '@/common/constants/constants';
import { ActiveStatusCodes, StatusCode } from '@/common/constants/status';
import { TodoStatusCode } from '@/common/constants/todo';
import { usePendingScrollStore } from '@/common/stores/pendingScrollStore';
import {
    FeedbackRequestWithDetails,
    Goal,
    Project,
    Todo,
} from '@/common/types/databaseTypes';
import { arraysEqual, getTodoStatus } from '@/common/utils/utils';
import useDailyScrumModalStore from '@/features/dailyscrum/hooks/useDailyScrumModalStore';
import useFeedbackModalStore from '@/features/performance/feedback/hooks/useFeedbackModalStore';
import {
    FEEDBACK_KEYS,
    useReceivedFeedbackRequests,
} from '@/features/performance/feedback/services/useFeedbackQueries';
import { teamService } from '@/features/team/services/teamService';
import { useCurrentWorkspace } from '@/features/workspace';
import { useMyWorkspaces } from '@/features/workspace/services/useWorkspaceQueries';
import { useAuthStore } from '@/stores/authStore';
import { usePlannerStore } from '@/stores/plannerStore';
import { useTeamStore } from '@/stores/teamStore';
import { useTodoDrawerStore } from '@/stores/todoDrawerStore';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
    CommentContent,
    CommentMentionContent,
    DailyScrumCommentContent,
    FeedbackCommentContent,
    FeedbackReceivedContent,
    FeedbackRequestContent,
    MentionContent,
    NotificationContent,
    NotificationType,
    ReplyContent,
    ReportContent,
    UpdateContext,
    WorkspaceInviteContent,
} from '../constants/notification';

/**
 * 데이터 업데이트 전후 값이 변경되었는지 확인하는 함수
 * 변경 여부 확인 후 알림을 보내야 할지 결정
 * @param 업데이트 전의 특정 값 (ex. goal.status_code)
 * @param 업데이트 후의 특정 값
 * @returns boolean
 */
export const isDifferent = (a, b) => {
    if (a === undefined || b === undefined) return false;

    if (Array.isArray(a) && Array.isArray(b)) {
        return !arraysEqual(a, b);
    }

    return a !== b;
};

/**
 * 할 일 상태에 따라 알림 발송 여부를 결정하는 함수
 * @param todo
 * @param changeTypes 변경 유형
 * @returns 알림을 발송해야 하는지 여부
 */
export const checkSendNotificationByTodoStatus = (
    todo: Todo,
    changeTypes: UpdateContext<Todo>['changeTypes']
): boolean => {
    // 상태 변경 알림은 항상 발송
    if (changeTypes.isStatusChange) {
        return true;
    }

    // 그 외 알림은 진행중 상태일 때만 발송
    const todoStatus = getTodoStatus(todo);
    return todoStatus === TodoStatusCode.IN_PROGRESS;
};

/**
 * 목표/프로젝트 상태에 따라 알림 발송 여부를 결정하는 함수
 * @param data/Project 목표/프로젝트 객체
 * @param changeTypes 변경 유형
 * @returns 알림을 발송해야 하는지 여부
 */
export const checkSendNotificationByStatusCode = <T extends Goal | Project>(
    data: T,
    changeTypes: UpdateContext<T>['changeTypes']
): boolean => {
    // 상태 변경 알림은 항상 발송
    if (changeTypes.isStatusChange) {
        return true;
    }

    // 그 외 알림은 활성화 상태일 때만 발송
    return ActiveStatusCodes.includes(data.status_code as StatusCode);
};

/**
 * 알림 메시지를 보여줄 때, 글자수를 제한하고 HTML 특수 문자를 이스케이프 처리하는 함수
 * @param message 알림 메시지
 * @returns 처리된 메시지
 */
export const limitNotificationMessage = (message: string): string => {
    if (!message) return '';

    // 특수 문자를 대체 문자나 유니코드로 변환
    const replaced = message
        .replace(/</g, '〈') // 유니코드 좌괄호로 대체
        .replace(/>/g, '〉'); // 유니코드 우괄호로 대체
    // .replace(/\*/g, '★'); // 별표는 별 기호로 대체

    const maxLength = MAX_NOTIFICATION_DISPLAY_LENGTH;
    return replaced.length > maxLength
        ? replaced.slice(0, maxLength) + '...'
        : replaced;
};

/**
 * 알림 컨텐츠를 저장할 때, 글자수 제한에 맞게 컨텐츠를 수정하는 함수 (필요 이상으로 긴 텍스트 저장 방지)
 * @param content 알림 컨텐츠
 * @returns 수정된 컨텐츠
 */
export const limitNotificationContentLength = (content: string): string => {
    if (!content) return '';

    const maxLength = MAX_NOTIFICATION_DISPLAY_LENGTH + 1;
    // 모든 종류의 줄바꿈 문자를 공백으로 대체
    const formattedContent = content.replace(/[\r\n]+/g, ' ');

    return formattedContent.length > maxLength
        ? formattedContent.slice(0, maxLength) + '...'
        : formattedContent;
};

/**
 * 알림 타입별 타입 라벨 반환 훅
 * @param type 알림 타입
 * @param content 알림 내용
 * @returns 타입 라벨
 */
export const useNotificationTypeLabel = (
    type: NotificationType,
    content: NotificationContent
): string => {
    const { t } = useTranslation('notification');

    const typeLabel =
        type === NotificationType.COMMENT
            ? t(`typeLabel.${type}.${(content as CommentContent).parent_type}`)
            : type === NotificationType.MENTION
              ? t(
                    `typeLabel.${type}.${(content as MentionContent).context_type}`
                )
              : type === NotificationType.COMMENT_MENTION
                ? t(
                      `typeLabel.${type}.${(content as CommentMentionContent).parent_type}`
                  )
                : t(`typeLabel.${type}`);

    return typeLabel;
};

/**
 * 알림 타입별 클릭 액션 핸들러
 * @param type 알림 타입
 * @param relatedId 관련 아이디
 * @param relatedType 관련 타입
 * @returns 클릭 액션 핸들러
 */
export const useNotificationActionHandler = () => {
    const queryClient = useQueryClient();

    const { user } = useAuthStore.getState();
    const { data: workspaces = [] } = useMyWorkspaces();
    const { currentWorkspace, setCurrentWorkspace } = useCurrentWorkspace(
        user?.id
    );
    const { setCurrentTeam, teams } = useTeamStore();
    const navigate = useNavigate();
    const { setScrollTarget } = usePendingScrollStore();

    const { openDrawer } = useTodoDrawerStore();
    const { openModal: openFeedbackModal } = useFeedbackModalStore();
    const { openModal: openDailyScrumModal } = useDailyScrumModalStore();

    const {
        dateActions: { setSelectedDate },
    } = usePlannerStore();

    const { data: feedbackRequests = [] } = useReceivedFeedbackRequests(
        user?.id,
        currentWorkspace?.id
    );

    // 워크스페이스 초대 처리 - 수락/거절 화면으로 이동
    const handleWorkspaceInvite = useCallback(
        ({ content }: { content: NotificationContent }) => {
            const invitationId = (content as WorkspaceInviteContent)
                .invitation_id;
            void navigate(`/invite/${invitationId}`);
        },
        [navigate]
    );
    // 워크스페이스에 새 멤버가 조인 - 세팅(멤버 관리)로 이동
    const handleWorkspaceJoin = useCallback(
        (workspaceId: string) => {
            void navigate(`/workspace/${workspaceId}/setting`);
        },
        [navigate]
    );

    // 팀 초대 처리
    const handleTeamInvite = useCallback(
        async (teamId: string) => {
            try {
                const teamData = await queryClient.fetchQuery({
                    queryKey: ['team', teamId],
                    queryFn: () => teamService.getTeamById(teamId),
                });
                await setCurrentTeam(teamData);
                // void navigate(`/team/${teamId}`);
                void navigate(`/team/${teamId}/goal`);
            } catch (error) {
                console.error('팀 조회 오류:', error);
            }
        },
        [setCurrentTeam, navigate, queryClient]
    );

    // 할 일 관련 알림 클릭 처리
    const handleTodoNotification = useCallback(
        (todoId: string) => {
            openDrawer({
                todoId,
                // 다른 사람 투두 알림도 받게 변경될 경우 정확히 투두 주인 아이디를 전달하도록 수정해야 함
                targetUserId: user?.id || '',
            });
        },
        [openDrawer, user]
    );

    // 목표 관련 알림 클릭 처리
    const handleGoalNotification = useCallback(
        (goalId: string) => {
            void navigate(`/goal/${goalId}`);
        },
        [navigate]
    );

    // 프로젝트 관련 알림 클릭 처리
    const handleProjectNotification = useCallback(
        (projectId: string) => {
            void navigate(`/project/${projectId}`);
        },
        [navigate]
    );

    // 노트 관련 알림 클릭 처리
    const handleNoteNotification = useCallback(
        (noteId: string) => {
            void navigate(`/note/${noteId}`);
        },
        [navigate]
    );

    // 댓글/답글 알림 클릭 처리
    const handleCommunicationNotification = useCallback(
        ({
            type,
            related_id,
            content,
        }: {
            type: NotificationType;
            related_id: string;
            content: NotificationContent;
        }) => {
            if (!related_id) return;

            let parentType: string;
            let parentId: string;

            if (type === NotificationType.COMMENT) {
                const commentContent = content as CommentContent;
                parentType = commentContent.parent_type;
                parentId = commentContent.parent_id;
            } else if (type === NotificationType.REPLY) {
                const replyContent = content as ReplyContent;
                parentType = replyContent.parent_type;
                parentId = replyContent.parent_id;
            } else if (type === NotificationType.COMMENT_MENTION) {
                const commentMentionContent = content as CommentMentionContent;
                parentType = commentMentionContent.parent_type;
                parentId = commentMentionContent.parent_id;
            } else {
                console.error('지원되지 않는 커뮤니케이션 알림 타입:', type);
                return;
            }

            // parent_type에 따라 적절한 페이지로 이동
            switch (parentType) {
                case 'todo':
                    handleTodoNotification(parentId);
                    break;
                case 'goal':
                    handleGoalNotification(parentId);
                    break;
                case 'project':
                    handleProjectNotification(parentId);
                    break;
                case 'note':
                    handleNoteNotification(parentId);
                    break;
                default:
                    console.log('처리되지 않은 부모 타입:', parentType);
            }
        },
        [
            handleTodoNotification,
            handleGoalNotification,
            handleProjectNotification,
            handleNoteNotification,
        ]
    );

    // 보고서 관련 알림 클릭 처리 - 보고서 페이지로 이동
    const handleReportNotification = useCallback(
        (reportId: string, workspaceId: string) => {
            if (currentWorkspace?.id === workspaceId) {
                void navigate(`/workspace/${workspaceId}/report/${reportId}`);
            } else {
                setCurrentWorkspace(
                    workspaces.find(ws => ws.id === workspaceId)
                );
                void navigate(`/workspace/${workspaceId}/report/${reportId}`);
            }
        },
        [navigate, currentWorkspace, setCurrentWorkspace, workspaces]
    );

    // 피드백 관련 알림 클릭 처리 (피드백 댓글 포함) - 피드백 페이지로 이동 후 탭 변경 또는 모달 오픈
    const handleFeedbackNotification = useCallback(
        async (
            workspaceId: string,
            tabName: string,
            content: NotificationContent
        ) => {
            const requestId = (content as FeedbackRequestContent).request_id;
            const type =
                tabName === TAB_RECEIVED_FEEDBACK ||
                tabName === TAB_SENT_FEEDBACK
                    ? 'feedback'
                    : 'feedback_request';
            const scrollTargetId =
                type === 'feedback'
                    ? (content as FeedbackReceivedContent).feedback_id
                    : (content as FeedbackRequestContent).request_id;

            if (currentWorkspace?.id === workspaceId) {
                // 같은 워크스페이스면 바로 페이지 이동
                void navigate(
                    `/workspace/${workspaceId}/feedback?tab=${tabName}`
                );

                // 스크롤 위치 조정
                setScrollTarget({
                    type,
                    id: scrollTargetId,
                });

                if (tabName === TAB_RECEIVED_REQUEST) {
                    const request = feedbackRequests.find(
                        req => req.id === requestId
                    );

                    if (request) {
                        openFeedbackModal('respond', { data: request });
                    }
                }
            } else {
                // 다른 워크스페이스면 전환 후 데이터 로드하고 모달 열기
                const targetWorkspace = workspaces.find(
                    ws => ws.id === workspaceId
                );
                if (!targetWorkspace) return;

                setCurrentWorkspace(targetWorkspace);

                // 페이지 이동
                await navigate(
                    `/workspace/${workspaceId}/feedback?tab=${tabName}`
                );

                // 스크롤 위치 조정
                setScrollTarget({
                    type,
                    id: scrollTargetId,
                });

                // 피드백 요청인 경우 데이터 다시 가져와서 모달 열기
                if (tabName === TAB_RECEIVED_REQUEST && requestId) {
                    try {
                        // 워크스페이스 변경 후 데이터 리프레시
                        await queryClient.refetchQueries({
                            queryKey: [
                                ...FEEDBACK_KEYS.receivedRequests(
                                    user?.id,
                                    workspaceId
                                ),
                            ],
                        });

                        // 새로 가져온 데이터에서 요청 찾기
                        const updatedRequests =
                            queryClient.getQueryData<
                                FeedbackRequestWithDetails[]
                            >([
                                ...FEEDBACK_KEYS.receivedRequests(
                                    user?.id,
                                    workspaceId
                                ),
                            ]) || [];

                        const request = updatedRequests.find(
                            req => req.id === requestId
                        );

                        if (request) {
                            openFeedbackModal('respond', { data: request });
                        }
                    } catch (error) {
                        console.error('피드백 요청 데이터 로드 중 오류', error);
                    }
                }
            }
        },
        [
            currentWorkspace?.id,
            navigate,
            setScrollTarget,
            feedbackRequests,
            openFeedbackModal,
            workspaces,
            setCurrentWorkspace,
            queryClient,
            user?.id,
        ]
    );

    // 데일리스크럼 관련 알림 클릭 처리
    const handleDailyScrumNotification = useCallback(
        async (teamId: string, dailyScrumDate: string) => {
            const team = teams.find(team => team.id === teamId);
            await setCurrentTeam(team);

            // navigate and open modal
            await navigate(`/team/${teamId}/member`);
            openDailyScrumModal({
                teamId,
                selectedDate: new Date(dailyScrumDate),
            });
        },
        [navigate, openDailyScrumModal, setCurrentTeam, teams]
    );

    // 계획 수립 알림 클릭 처리 - 오늘 날짜의 플래너 페이지로 이동
    const handlePlannerNotification = useCallback(() => {
        setSelectedDate(new Date());
        void navigate(`/planner`);
    }, [navigate, setSelectedDate]);

    // 알림 타입별 처리 분기
    return useCallback(
        async ({
            type,
            related_id,
            content,
        }: {
            type: NotificationType;
            related_id: string;
            content: NotificationContent;
        }) => {
            if (!related_id) return;

            switch (type) {
                case NotificationType.WORKSPACE_INVITE:
                    void handleWorkspaceInvite({ content });
                    break;

                case NotificationType.WORKSPACE_JOIN:
                    void handleWorkspaceJoin(related_id);
                    break;

                case NotificationType.TEAM_INVITE:
                    void handleTeamInvite(related_id);
                    break;

                case NotificationType.TODO_STATE_CHANGED:
                case NotificationType.TODO_DEADLINE_CHANGED:
                case NotificationType.TODO_ASSIGNED:
                case NotificationType.TODO_TIME_CHANGED:
                case NotificationType.TODO_DEADLINE_REMINDER:
                    handleTodoNotification(related_id);
                    break;

                case NotificationType.GOAL_STATE_CHANGED:
                case NotificationType.GOAL_PRIORITY_CHANGED:
                case NotificationType.GOAL_LEADER_CHANGED:
                case NotificationType.GOAL_DEADLINE_CHANGED:
                case NotificationType.GOAL_DEADLINE_REMINDER:
                    handleGoalNotification(related_id);
                    break;

                case NotificationType.PROJECT_STATE_CHANGED:
                case NotificationType.PROJECT_PRIORITY_CHANGED:
                case NotificationType.PROJECT_LEADER_CHANGED:
                case NotificationType.PROJECT_DEADLINE_CHANGED:
                case NotificationType.PROJECT_DEADLINE_REMINDER:
                    handleProjectNotification(related_id);
                    break;

                case NotificationType.COMMENT:
                case NotificationType.REPLY:
                case NotificationType.COMMENT_MENTION:
                    handleCommunicationNotification({
                        type,
                        related_id,
                        content,
                    });
                    break;

                case NotificationType.MENTION:
                    handleNoteNotification(related_id);
                    break;

                case NotificationType.REPORT_RECEIVE:
                case NotificationType.REPORT_UPDATED:
                case NotificationType.REPORT_REQUEST: {
                    const workspaceId = (content as ReportContent).workspace_id;
                    handleReportNotification(related_id, workspaceId);
                    break;
                }

                // 피드백 댓글은 여기서 함께 처리
                case NotificationType.FEEDBACK_COMMENT:
                case NotificationType.FEEDBACK_REACTION:
                case NotificationType.FEEDBACK_RECEIVED:
                case NotificationType.FEEDBACK_UPDATED:
                case NotificationType.FEEDBACK_REQUEST:
                case NotificationType.FEEDBACK_REQUEST_UPDATED:
                case NotificationType.FEEDBACK_REQUEST_REJECTED: {
                    const workspaceId = (content as FeedbackCommentContent)
                        .workspace_id;

                    // 알림 타입에 따른 탭 인덱스 결정
                    let tabName = TAB_RECEIVED_FEEDBACK; // 기본값

                    // 알림 타입별 처리
                    switch (type) {
                        case NotificationType.FEEDBACK_COMMENT:
                        case NotificationType.FEEDBACK_REACTION: {
                            // 댓글, 리액션은 피드백 수신자 여부에 따라 구분
                            const isFeedbackReceiver = (
                                content as FeedbackCommentContent
                            ).is_feedback_receiver;
                            tabName = isFeedbackReceiver
                                ? TAB_RECEIVED_FEEDBACK
                                : TAB_SENT_FEEDBACK;
                            break;
                        }

                        case NotificationType.FEEDBACK_REQUEST:
                        case NotificationType.FEEDBACK_REQUEST_UPDATED:
                            tabName = TAB_RECEIVED_REQUEST;
                            break;

                        case NotificationType.FEEDBACK_REQUEST_REJECTED:
                            tabName = TAB_SENT_REQUEST;
                            break;

                        default:
                            tabName = TAB_RECEIVED_FEEDBACK;
                            break;
                    }

                    await handleFeedbackNotification(
                        workspaceId,
                        tabName,
                        content
                    );
                    break;
                }

                // 데일리스크럼 댓글
                case NotificationType.DAILY_SCRUM_COMMENT: {
                    const teamId = (content as DailyScrumCommentContent)
                        .team_id;
                    await handleDailyScrumNotification(
                        teamId,
                        (content as DailyScrumCommentContent).daily_scrum_date
                    );
                    break;
                }

                case NotificationType.PLAN_TIME:
                case NotificationType.PLAN_REQUEST:
                    handlePlannerNotification();
                    break;

                // 다른 알림 타입 처리...
                default:
                    console.log('처리되지 않은 알림 타입:', type);
            }
        },
        [
            handleWorkspaceInvite,
            handleWorkspaceJoin,
            handleTeamInvite,
            handleTodoNotification,
            handleGoalNotification,
            handleProjectNotification,
            handleCommunicationNotification,
            handleNoteNotification,
            handleFeedbackNotification,
            handleDailyScrumNotification,
            handlePlannerNotification,
            handleReportNotification,
        ]
    );
};
